/**
 * Demande d'accompagnement — constantes d'affichage (labels, badges, options).
 *
 * Données pures (pas d'icône Vue ici — le mapping statut→icône vit dans
 * `AccompStatusBadge.vue`). Couleurs via tokens DaisyUI sémantiques (cf MASTER §2),
 * jamais de hex en dur.
 */
import type {
  AccompActionType,
  AccompActorKind,
  AccompDocument,
  AccompRequestType,
  AccompScope,
  AccompStatus,
  AccompUrgency,
} from '@/v2/accompagnement/api/accompagnementTypes'

interface BadgeMeta {
  label: string
  /** Classe DaisyUI appliquée au `badge` (sémantique, pas de couleur seule). */
  badgeClass: string
}

interface Option<T extends string> {
  value: T
  label: string
}

// — Statut —

/** Ordre logique du cycle de vie (filtres, tri d'affichage). */
export const ACCOMP_STATUS_ORDER: AccompStatus[] = [
  'NOUVELLE',
  'ATTRIBUEE',
  'EN_COURS',
  'GAGNEE',
  'PERDUE',
  'ANNULEE',
]

export const ACCOMP_STATUS_META: Record<AccompStatus, BadgeMeta> = {
  NOUVELLE: { label: 'Nouvelle', badgeClass: 'badge-info' },
  ATTRIBUEE: { label: 'Attribuée', badgeClass: 'badge-warning' },
  EN_COURS: { label: 'En cours', badgeClass: 'badge-primary' },
  GAGNEE: { label: 'Gagnée', badgeClass: 'badge-success' },
  PERDUE: { label: 'Perdue', badgeClass: 'badge-error' },
  ANNULEE: { label: 'Annulée', badgeClass: 'badge-ghost' },
}

export const ACCOMP_STATUS_OPTIONS: Option<AccompStatus>[] = ACCOMP_STATUS_ORDER.map((value) => ({
  value,
  label: ACCOMP_STATUS_META[value].label,
}))

// — Niveau d'urgence —

export const ACCOMP_URGENCY_ORDER: AccompUrgency[] = ['NORMAL', 'PRIORITAIRE']

/** Poids pour le tri (PRIORITAIRE en tête). */
export const ACCOMP_URGENCY_WEIGHT: Record<AccompUrgency, number> = {
  NORMAL: 0,
  PRIORITAIRE: 1,
}

export const ACCOMP_URGENCY_META: Record<AccompUrgency, BadgeMeta> = {
  NORMAL: { label: 'Normal', badgeClass: 'badge-ghost' },
  PRIORITAIRE: { label: 'Prioritaire', badgeClass: 'badge-error' },
}

export const ACCOMP_URGENCY_OPTIONS: Option<AccompUrgency>[] = ACCOMP_URGENCY_ORDER.map((value) => ({
  value,
  label: ACCOMP_URGENCY_META[value].label,
}))

// — Type de demande —

export const ACCOMP_TYPE_LABEL: Record<AccompRequestType, string> = {
  NOUVELLE_VENTE: 'Nouvelle vente',
  EVOLUTION: 'Évolution',
  RENOUVELLEMENT: 'Renouvellement',
  MIGRATION: 'Migration',
}

export const ACCOMP_TYPE_OPTIONS: Option<AccompRequestType>[] = (
  Object.keys(ACCOMP_TYPE_LABEL) as AccompRequestType[]
).map((value) => ({ value, label: ACCOMP_TYPE_LABEL[value] }))

// — Périmètre (sujets concernés) —

export const ACCOMP_SCOPE_LABEL: Record<AccompScope, string> = {
  INTERNET: 'Internet',
  RESEAU: 'Réseau',
  TELEPHONIE: 'Téléphonie',
  M365: 'Microsoft 365',
  WIFI: 'WiFi',
  CYBERSECURITE: 'Cybersécurité',
  SAUVEGARDE: 'Sauvegarde',
  HEBERGEMENT: 'Hébergement',
  SERVEURS: 'Serveurs',
  POSTE_TRAVAIL: 'Poste de travail',
  IMPRESSION: 'Impression',
  SUPERVISION: 'Supervision',
  INFOGERANCE: 'Infogérance',
  AUTRE: 'Autre',
}

export const ACCOMP_SCOPE_OPTIONS: Option<AccompScope>[] = (
  Object.keys(ACCOMP_SCOPE_LABEL) as AccompScope[]
).map((value) => ({ value, label: ACCOMP_SCOPE_LABEL[value] }))

// — Documents disponibles —

export const ACCOMP_DOCUMENT_LABEL: Record<AccompDocument, string> = {
  FACTURE_OPERATEUR: 'Facture opérateur',
  SCHEMA_RESEAU: 'Schéma réseau',
  CONTRAT_ACTUEL: 'Contrat actuel',
  PHOTOS_BAIE: 'Photos baie informatique',
  PHOTOS_LOCAUX: 'Photos locaux',
  EXPORT_TELEPHONIE: 'Export téléphonie',
  INVENTAIRE_MATERIEL: 'Inventaire matériel',
  PLANS_BATIMENTS: 'Plans des bâtiments',
}

export const ACCOMP_DOCUMENT_OPTIONS: Option<AccompDocument>[] = (
  Object.keys(ACCOMP_DOCUMENT_LABEL) as AccompDocument[]
).map((value) => ({ value, label: ACCOMP_DOCUMENT_LABEL[value] }))

// — Contexte : libellés à choisir (hypothèses, à confirmer DT) —

export const ACCOMP_REMOTE_WORK_OPTIONS: string[] = ['Aucun', 'Ponctuel', 'Régulier', 'Télétravail total']
export const ACCOMP_GROWTH_OPTIONS: string[] = ['Stable', 'Faible', 'Modérée', 'Forte']

// — Actions d'accompagnement —

export const ACCOMP_ACTION_TYPE_LABEL: Record<AccompActionType, string> = {
  POINT_COMMERCIAL: 'Point avec le commercial',
  POINT_CLIENT: 'Point avec le client',
  REUNION: 'Réunion',
  DEPLACEMENT: 'Déplacement sur site',
  ETUDE_FAISABILITE: 'Étude de faisabilité',
  AUTRE: 'Autre',
}

export const ACCOMP_ACTION_TYPE_OPTIONS: Option<AccompActionType>[] = (
  Object.keys(ACCOMP_ACTION_TYPE_LABEL) as AccompActionType[]
).map((value) => ({ value, label: ACCOMP_ACTION_TYPE_LABEL[value] }))

export const ACCOMP_ACTOR_KIND_LABEL: Record<AccompActorKind, string> = {
  INTERNE: 'Interne',
  CONTACT: 'Contact client',
  EXTERNE: 'Intervenant externe',
}

export const ACCOMP_ACTOR_KIND_OPTIONS: Option<AccompActorKind>[] = (
  Object.keys(ACCOMP_ACTOR_KIND_LABEL) as AccompActorKind[]
).map((value) => ({ value, label: ACCOMP_ACTOR_KIND_LABEL[value] }))
